import { useTheme } from '@emotion/react';
import { IconButton, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import ProfileImageUploader from '../../../../components/ProfileImageUploader.js/ProfileImageUploader';
import { useDispatch, useSelector } from 'react-redux';
import CompanyInfoItem from './CompanyInfoItem';
import uploadFileToS3 from '../../../../utils/fileUpload';
import appapi from '../../../../api/apiClient';
import { setLoading } from '../../../../redux/features/appSlice';
import { checkPermission, setCompanyDetails } from '../../../../redux/features/authSlice';
import profileDefault from '../../../../assets/images/defaultLogo.png';
import { useTranslation } from 'react-i18next';

export default function CompanyDetailsCard({ onClickEdit }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const text = theme.palette.primary.text;
  const textLight = theme.palette.primary.textLight;

  const { companyDetails, showerror, user } = useSelector((state) => state.auth);
  const settingPermission = useSelector((state) => checkPermission(state, 'Settings', 'Manage Company Profile'));

  const { t } = useTranslation();

  const handleFile = async (file) => {
    if (file) {
      dispatch(setLoading(true));
      try {
        const url = await uploadFileToS3(file);
        const ep = user?.type === 'trader' ? `/trader/uploadTraderLogo/${companyDetails._id}` : `/company-management/logo/${companyDetails._id}`;
        const response = await appapi.put(ep, {
          image: url,
          companyLogo: url,
        });
        if (response.ok) {
          const data = response.data;
          if (data.status === '7400') {
            dispatch(setCompanyDetails({ ...companyDetails, companyLogo: url }));
          }
          dispatch(setLoading(false));
        } else {
          dispatch(setLoading(false));
        }
      } catch (e) {
        dispatch(setLoading(false));
      }
    }
  };

  return (
    <Stack
      flex={1}
      style={{ backgroundColor: '#F2F3F580', borderRadius: '8px', padding: '16px', borderColor: '#8993AC26', borderWidth: '1px' }}
      className="overflow-auto"
    >
      <Typography
        className="fs-16 ff-Roboto fw-regular !capitalize"
        color={text}
      >
        {t('Company details')}
      </Typography>
      <Typography
        className="fs-12 ff-Roboto fw-regular"
        color={textLight}
      >
        {t('Some info may be visible to other people using ZeroEmission services')}
      </Typography>
      <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        className="mt-[32px]"
      >
        <Typography
          className="fs-12 ff-Roboto fw-regular"
          color={textLight}
        >
          {t('Company logo')}
        </Typography>
        {settingPermission.editAccess ? (
          <ProfileImageUploader
            url={companyDetails?.companyLogo ? companyDetails?.companyLogo : profileDefault}
            inputId={`doc`}
            handleFile={(files) => handleFile(files)}
            showFileName={false}
            width={'64px'}
            height={'64px'}
            iconViewHeight={'16px'}
            iconMargin={'50px'}
            iconSize={'10px'}
          />
        ) : companyDetails?.companyLogo ? (
          <img src={companyDetails?.companyLogo ? companyDetails?.companyLogo : profileDefault} />
        ) : null}
      </Stack>
      <CompanyInfoItem
        title={t('Company name')}
        value={user?.type === 'trader' ? companyDetails?.traderName : companyDetails?.companyName}
        showEditIcon={false}
      />
      <CompanyInfoItem
        title={t('Display name')}
        value={companyDetails?.displayName}
        showEditIcon={false}
      />
      {user?.type !== 'trader' && (
        <CompanyInfoItem
          title={t('Company type')}
          value={companyDetails?.companyType}
          showEditIcon={false}
        />
      )}
      {user?.type !== 'trader' && (
        <CompanyInfoItem
          title={t('Union Registry Account')}
          value={companyDetails?.unionRegistryAccount}
          showEditIcon={settingPermission?.editAccess}
          onClickEdit={() => onClickEdit()}
        />
      )}
      {user?.type !== 'trader' && (
        <CompanyInfoItem
          showError={showerror}
          title={t('Legal entity type')}
          value={companyDetails?.entityType}
          showEditIcon={settingPermission?.editAccess}
          onClickEdit={() => onClickEdit()}
        />
      )}
      <CompanyInfoItem
        showError={showerror}
        capitalize={false}
        title={t('Website')}
        value={user?.type === 'trader' ? companyDetails?.traderWebsite : companyDetails?.companyWebsite}
        showEditIcon={settingPermission?.editAccess}
        onClickEdit={() => onClickEdit()}
      />
      <CompanyInfoItem
        showError={showerror}
        title={t('Address')}
        value={user?.type === 'trader' ? companyDetails?.traderAddress : companyDetails?.regAddress}
        showEditIcon={settingPermission?.editAccess}
        onClickEdit={() => onClickEdit()}
      />
      <CompanyInfoItem
        showError={showerror}
        title={t('State/Province')}
        value={companyDetails?.stateName}
        showEditIcon={settingPermission?.editAccess}
        onClickEdit={() => onClickEdit()}
      />
      <CompanyInfoItem
        showError={showerror}
        title={t('Country')}
        value={companyDetails?.countryName}
        showEditIcon={settingPermission?.editAccess}
        onClickEdit={() => onClickEdit()}
      />
      <CompanyInfoItem
        showError={showerror}
        title={t('Zip Code')}
        value={companyDetails?.zipCode}
        showEditIcon={settingPermission?.editAccess}
        onClickEdit={() => onClickEdit()}
      />
      {user?.type !== 'trader' && (
        <CompanyInfoItem
          title={t('Actual Initial EUA Balance')}
          value={companyDetails?.euaBalance}
          showEditIcon={settingPermission?.editAccess}
          onClickEdit={() => onClickEdit()}
        />
      )}
    </Stack>
  );
}
